/**
 * My Career Profile (Employee) — Phase 1
 *
 * Profil dynamique : aspirations, mobilité, projets, certifications, historique poste.
 * L'employé édite lui-même ; lecture autorisée pour Manager / Admin.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  User, Award, Briefcase, History, Compass, Target,
  Plus, Trash2, Save, ExternalLink, FolderKanban, Sparkles,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const MOBILITY_WISH_OPTIONS = [
  { value: 'no_change', label: 'Pas de changement souhaité' },
  { value: 'horizontal', label: 'Mobilité horizontale (changement de métier)' },
  { value: 'vertical', label: 'Mobilité verticale (promotion)' },
  { value: 'geographic', label: 'Mobilité géographique' },
  { value: 'mixed', label: 'Mixte (vertical + géographique)' },
];

// ─────────────────────────────────────────────────────────────
// Certification Dialog
// ─────────────────────────────────────────────────────────────
function CertDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ name: '', issuer: '', issuedDate: '', expirationDate: '', credentialUrl: '' });
  useEffect(() => { if (open) setForm({ name: '', issuer: '', issuedDate: '', expirationDate: '', credentialUrl: '' }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Ajouter une certification</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex : AWS Solutions Architect" data-testid="cert-name" /></div>
          <div><Label>Organisme</Label><Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="ex : Amazon Web Services" data-testid="cert-issuer" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date d'obtention</Label><Input type="date" value={form.issuedDate} onChange={(e) => setForm({ ...form, issuedDate: e.target.value })} data-testid="cert-issued" /></div>
            <div><Label>Expiration</Label><Input type="date" value={form.expirationDate} onChange={(e) => setForm({ ...form, expirationDate: e.target.value })} /></div>
          </div>
          <div><Label>Lien (URL)</Label><Input value={form.credentialUrl} onChange={(e) => setForm({ ...form, credentialUrl: e.target.value })} placeholder="https://..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={() => { if (!form.name.trim()) { toast.error('Nom requis'); return; } onSubmit(form); onOpenChange(false); }}
            data-testid="cert-save-btn"
          >Ajouter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Project Dialog
// ─────────────────────────────────────────────────────────────
function ProjectDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ title: '', description: '', role: '', startDate: '', endDate: '' });
  useEffect(() => { if (open) setForm({ title: '', description: '', role: '', startDate: '', endDate: '' }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Ajouter un projet</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="ex : Migration ERP" data-testid="proj-title" /></div>
          <div><Label>Mon rôle</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="ex : Lead développeur" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Début</Label><Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></div>
            <div><Label>Fin</Label><Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={() => { if (!form.title.trim()) { toast.error('Titre requis'); return; } onSubmit(form); onOpenChange(false); }}
            data-testid="proj-save-btn"
          >Ajouter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function MyCareerProfilePage() {
  const { user } = useAuth();
  const { employeeId: routeEmployeeId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAspirations, setSavingAspirations] = useState(false);
  const [aspirations, setAspirations] = useState({
    careerAspirations: '',
    mobilityWish: 'no_change',
    targetTimeframeMonths: 12,
    isAvailableForInternalMobility: false,
    targetJobProfileIds: [],
  });
  const [jobProfiles, setJobProfiles] = useState([]);
  const [certDialog, setCertDialog] = useState(false);
  const [projDialog, setProjDialog] = useState(false);
  const [employeeId, setEmployeeId] = useState(routeEmployeeId || null);

  // Si pas d'ID dans l'URL, on tente de retrouver l'employé courant
  const resolveSelfEmployee = useCallback(async () => {
    try {
      // Essayer d'abord par userId
      const res = await api.get('/employees', { params: { limit: 1000 } });
      const list = res.data?.employees || res.data || [];
      const userEmail = (user?.email || '').toLowerCase();
      const me = list.find(e =>
        e.userId === user?.id ||
        (e.email && e.email.toLowerCase() === userEmail) ||
        (user?.matricule && e.matricule === user.matricule)
      );
      if (me) setEmployeeId(me.id);
    } catch (e) {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    if (!employeeId && user) resolveSelfEmployee();
  }, [employeeId, user, resolveSelfEmployee]);

  const fetchProfile = useCallback(async () => {
    if (!employeeId) return;
    try {
      const [profileRes, profilesRes] = await Promise.all([
        api.get(`/career/employees/${employeeId}/profile`),
        api.get('/skills-intelligence/job-profiles').catch(() => ({ data: { profiles: [] } })),
      ]);
      const p = profileRes.data;
      setProfile(p);
      setAspirations({
        careerAspirations: p.aspirations?.careerAspirations || '',
        mobilityWish: p.aspirations?.mobilityWish || 'no_change',
        targetTimeframeMonths: p.aspirations?.targetTimeframeMonths || 12,
        isAvailableForInternalMobility: !!p.aspirations?.isAvailableForInternalMobility,
        targetJobProfileIds: p.aspirations?.targetJobProfileIds || [],
      });
      setJobProfiles(profilesRes.data?.profiles || profilesRes.data?.items || profilesRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement du profil');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { if (employeeId) fetchProfile(); }, [employeeId, fetchProfile]);

  const saveAspirations = async () => {
    try {
      setSavingAspirations(true);
      await api.put(`/career/employees/${employeeId}/aspirations`, aspirations);
      toast.success('Aspirations enregistrées');
      fetchProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSavingAspirations(false);
    }
  };

  const addCertification = async (cert) => {
    try {
      await api.post(`/career/employees/${employeeId}/certifications`, cert);
      toast.success('Certification ajoutée');
      fetchProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const deleteCert = async (certId) => {
    if (!window.confirm('Supprimer cette certification ?')) return;
    try {
      await api.delete(`/career/employees/${employeeId}/certifications/${certId}`);
      toast.success('Supprimée');
      fetchProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const addProject = async (proj) => {
    try {
      await api.post(`/career/employees/${employeeId}/projects`, proj);
      toast.success('Projet ajouté');
      fetchProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const deleteProj = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await api.delete(`/career/employees/${employeeId}/projects/${id}`);
      toast.success('Supprimé');
      fetchProfile();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const toggleTargetProfile = (id) => {
    const current = aspirations.targetJobProfileIds || [];
    setAspirations({
      ...aspirations,
      targetJobProfileIds: current.includes(id) ? current.filter(x => x !== id) : [...current, id]
    });
  };

  if (loading) {
    return <DashboardLayout><div className="p-8 text-center text-slate-400">Chargement...</div></DashboardLayout>;
  }
  if (!profile) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <p className="text-slate-500">Aucun profil trouvé. {!employeeId && 'Contactez votre RH si vous n\'avez pas de fiche employé.'}</p>
        </div>
      </DashboardLayout>
    );
  }

  const { employee, currentJobProfile, certifications = [], projects = [], positionHistory = [], stats } = profile;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="career-profile-page">
        {/* Header */}
        <Card className="mb-6 overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-indigo-500 to-purple-600" />
          <CardContent className="p-6 -mt-12">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center text-2xl font-bold text-indigo-600">
                {employee.photoUrl ? (
                  <img src={employee.photoUrl} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  `${(employee.firstName || ' ').charAt(0)}${(employee.lastName || ' ').charAt(0)}`
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-slate-900">
                  {employee.firstName} {employee.lastName}
                </h1>
                <p className="text-slate-600">{employee.jobTitle || currentJobProfile?.name || '—'}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline">
                    <Briefcase className="w-3 h-3 mr-1" />
                    Matricule {employee.matricule || '—'}
                  </Badge>
                  <Badge variant="outline">
                    <Award className="w-3 h-3 mr-1" />
                    {stats?.skillsTracked || 0} compétence(s) suivie(s)
                  </Badge>
                  <Badge variant="outline">
                    <Target className="w-3 h-3 mr-1" />
                    {stats?.validatedEvaluations || 0} évaluation(s) validée(s)
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="aspirations" className="space-y-4">
          <TabsList className="bg-slate-100 flex-wrap">
            <TabsTrigger value="aspirations" data-testid="tab-aspirations"><Compass className="w-4 h-4 mr-2" />Aspirations</TabsTrigger>
            <TabsTrigger value="certifications" data-testid="tab-certifications"><Award className="w-4 h-4 mr-2" />Certifications ({certifications.length})</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects"><FolderKanban className="w-4 h-4 mr-2" />Projets ({projects.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-2" />Historique poste</TabsTrigger>
          </TabsList>

          {/* === ASPIRATIONS === */}
          <TabsContent value="aspirations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" />Mon projet professionnel</CardTitle>
                <CardDescription>Partagez vos aspirations pour qu'on vous accompagne au mieux</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Mes ambitions de carrière</Label>
                  <Textarea
                    rows={4}
                    placeholder="Décrivez vos objectifs professionnels, vos motivations, vos centres d'intérêt..."
                    value={aspirations.careerAspirations}
                    onChange={(e) => setAspirations({ ...aspirations, careerAspirations: e.target.value })}
                    data-testid="career-aspirations-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Type de mobilité souhaitée</Label>
                    <Select
                      value={aspirations.mobilityWish}
                      onValueChange={(v) => setAspirations({ ...aspirations, mobilityWish: v })}
                    >
                      <SelectTrigger data-testid="mobility-wish-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MOBILITY_WISH_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Horizon (mois)</Label>
                    <Input
                      type="number" min={0} max={120}
                      value={aspirations.targetTimeframeMonths}
                      onChange={(e) => setAspirations({ ...aspirations, targetTimeframeMonths: parseInt(e.target.value, 10) || 0 })}
                      data-testid="timeframe-input"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                  <div>
                    <p className="font-medium text-slate-900">Disponible pour la mobilité interne</p>
                    <p className="text-xs text-slate-500">Vous serez visible pour le matching automatique</p>
                  </div>
                  <Switch
                    checked={aspirations.isAvailableForInternalMobility}
                    onCheckedChange={(v) => setAspirations({ ...aspirations, isAvailableForInternalMobility: v })}
                    data-testid="mobility-available-switch"
                  />
                </div>

                {/* Postes ciblés */}
                {jobProfiles.length > 0 && (
                  <div>
                    <Label>Postes que je vise (optionnel)</Label>
                    <p className="text-xs text-slate-500 mb-2">Vous pouvez sélectionner plusieurs postes pour orienter votre plan de carrière</p>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 rounded-lg border border-slate-200">
                      {jobProfiles.map(p => {
                        const checked = aspirations.targetJobProfileIds?.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => toggleTargetProfile(p.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${checked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                            data-testid={`target-profile-${p.id}`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={saveAspirations} disabled={savingAspirations} data-testid="save-aspirations-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer mes aspirations
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === CERTIFICATIONS === */}
          <TabsContent value="certifications">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Mes certifications</CardTitle>
                  <CardDescription>Diplômes professionnels & badges obtenus</CardDescription>
                </div>
                <Button onClick={() => setCertDialog(true)} data-testid="add-cert-btn">
                  <Plus className="w-4 h-4 mr-2" />Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {certifications.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucune certification ajoutée</p>
                ) : (
                  <div className="space-y-2">
                    {certifications.map(c => (
                      <div key={c.id} className="flex items-start justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-500">
                            {c.issuer || '—'}
                            {c.issuedDate && ` · obtenue le ${new Date(c.issuedDate).toLocaleDateString('fr-FR')}`}
                            {c.expirationDate && ` · expire le ${new Date(c.expirationDate).toLocaleDateString('fr-FR')}`}
                          </p>
                          {c.credentialUrl && (
                            <a href={c.credentialUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 inline-flex items-center gap-1 mt-1">
                              <ExternalLink className="w-3 h-3" />Voir le badge
                            </a>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteCert(c.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === PROJETS === */}
          <TabsContent value="projects">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Mes projets</CardTitle>
                  <CardDescription>Réalisations significatives qui valorisent votre profil</CardDescription>
                </div>
                <Button onClick={() => setProjDialog(true)} data-testid="add-proj-btn">
                  <Plus className="w-4 h-4 mr-2" />Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {projects.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucun projet renseigné</p>
                ) : (
                  <div className="space-y-3">
                    {projects.map(p => (
                      <div key={p.id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{p.title}</p>
                            <p className="text-xs text-slate-500">
                              {p.role && `${p.role} · `}
                              {p.startDate && new Date(p.startDate).toLocaleDateString('fr-FR')}
                              {p.endDate && ` → ${new Date(p.endDate).toLocaleDateString('fr-FR')}`}
                            </p>
                            {p.description && <p className="text-sm text-slate-700 mt-1">{p.description}</p>}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteProj(p.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === HISTORIQUE === */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Mon historique poste</CardTitle>
                <CardDescription>Postes occupés (alimenté par votre RH)</CardDescription>
              </CardHeader>
              <CardContent>
                {positionHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Aucune entrée d'historique</p>
                ) : (
                  <div className="space-y-3">
                    {positionHistory.map(h => (
                      <div key={h.id} className="p-3 rounded-lg border border-slate-200">
                        <p className="font-medium text-slate-900">{h.title}</p>
                        <p className="text-xs text-slate-500">
                          {h.startDate && new Date(h.startDate).toLocaleDateString('fr-FR')}
                          {h.endDate ? ` → ${new Date(h.endDate).toLocaleDateString('fr-FR')}` : ' → en cours'}
                        </p>
                        {h.description && <p className="text-sm text-slate-700 mt-1">{h.description}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <CertDialog open={certDialog} onOpenChange={setCertDialog} onSubmit={addCertification} />
        <ProjectDialog open={projDialog} onOpenChange={setProjDialog} onSubmit={addProject} />
      </div>
    </DashboardLayout>
  );
}
